import { Component, effect, inject, OnInit, Renderer2 } from '@angular/core'
import { NavigationEnd, NavigationError, NavigationStart, Router, RouterOutlet, RoutesRecognized } from '@angular/router'
import { ThemeService } from './services/theme.service'
import { TranslationService } from './services/translation/translation.service'
import { SidePanelComponent } from './shared/side-panel/side-panel.component'
import { ToastContainer } from '@mua/toast'

/**
 * The root component of the Angular application, responsible for setting up the main
 * application structure and handling theme changes.
 *
 * This component utilizes Angular's dependency injection to access services related to
 * translation and theme management. It also manages the body's class based on the current
 * theme, allowing for dynamic styling of the application.
 *
 * @component AppComponent
 *
 * Dependencies:
 * - `RouterOutlet`: Allows for routing between different views in the application.
 * - `ThemeService`: Provides methods to manage and retrieve the current theme of the application.
 * - `TranslationService`: Handles translation and localization of the application's content.
 * - `Renderer2`: Angular service for manipulating the DOM, particularly for adding or removing classes on the body element.
 *
 * @class AppComponent
 */

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
    imports: [RouterOutlet, SidePanelComponent, ToastContainer]
})
export class AppComponent  {
    /**
     * Injects necessary services.

     * This section injects services for translation, theme management, and DOM manipulation.

     * @private
     * @type {TranslationService}
     */
    translationService: TranslationService = inject(TranslationService)
    /**
     * Injects the theme service.
     *
     * @private
     * @type {ThemeService}
     */
    themeService: ThemeService = inject(ThemeService)
    /**
     * Injects the renderer for DOM manipulation.
     *
     * @private
     * @type {Renderer2}
     */
    renderer: Renderer2 = inject(Renderer2)

    /**
     * Initializes the AppComponent and sets up the initial theme.
     *
     * This constructor injects the necessary services and adds the default
     * 'light' class to the body element. It sets up an effect to dynamically
     * update the body's class based on the current theme signal.
     *
     * @constructor
     */
    constructor() {
        this.renderer.addClass(document.body, 'light')

        /**
         * A reactive effect that updates the body's class based on the current theme.
         *
         * This effect runs whenever the theme signal changes, ensuring that the
         * appropriate theme class ('light' or 'dark') is added to or removed from
         * the body element.
         *
         * @effect
         */
        effect(() => {
            if (this.themeService.themeSignal() === 'light') {
                this.renderer.removeClass(document.body, 'dark')
            } else {
                this.renderer.removeClass(document.body, 'light')
            }
            this.renderer.addClass(document.body, this.themeService.themeSignal())
        })
    }


}
